import { useQueryClient } from 'react-query';

import { useCompany } from 'modules/app/hooks/useCompany';
import { getV2ApiCompanyIdCardsCardIdAggregatedActivity } from 'src/client/spendesk-api';
import { useInfiniteQuery } from 'src/core/api/hooks/useInfiniteQuery';
import { customQueryRequestErrorFactory } from 'src/core/api/queryError';
import { type CurrenciesKey } from 'src/core/config/money';

export type ActivityType =
  | 'ordered'
  | 'activated'
  | 'blocked'
  | 'unblocked'
  | 'paused'
  | 'unpaused'
  | 'lost'
  | 'stolen'
  | 'damaged'
  | 'cancelled'
  | 'sms-pin-sent'
  | 'sms-pin-set'
  | 'budget-changed'
  | 'usage-period-changed'
  | 'mcc-changed'
  | 'allow-withdrawal'
  | 'deny-withdrawal'
  | 'allow-onlineUsage'
  | 'deny-onlineUsage'
  | 'allow-contactless'
  | 'deny-contactless';

export type PhysicalCardLoadActivityType = 'top-up' | 'top-down' | 'unknown';

export type Activity = {
  aggregate_type: 'activity';
  id: string;
  activity_type: ActivityType;
  user: {
    fullname: string;
  };
  created_at: string;
};

export type Payment = {
  aggregate_type: 'payment';
  id: string;
  description: string;
  currency_declared: CurrenciesKey;
  currency_billed: CurrenciesKey;
  amount_declared: number;
  amount_billed: number;
  total_amount: number;
  fx_fee_amount: number;
  created_at: string;
  particularity?: 'atm_withdrawal' | 'visa_support';
  paid_at?: string;
  supplier?: {
    name: string;
    url?: string;
  };
};

export type FailedTransaction = {
  aggregate_type: 'failed_transaction';
  amount_billed: number;
  amount_declared: number;
  created_at: string;
  currency_billed: CurrenciesKey;
  currency_declared: CurrenciesKey;
  fx_fee_amount: number;
  id: string;
  merchant_name: string | null;
  reason_code: string;
  supplier?: { name: string; url?: string | null };
  total_amount: number;
};

export type PhysicalCardLoad = {
  aggregate_type: 'physical_load';
  amount: number | null;
  approvers: Array<{ fullname: string }> | null;
  auto_approved: boolean;
  available_after: number | null;
  available_before: number | null;
  created_at: string;
  description: string | null;
  id: string;
  user: { fullname: string };
};

type CardActivity = Activity | Payment | FailedTransaction | PhysicalCardLoad;

type RawAccountLoadResponse = {
  data: CardActivity[];
  meta: {
    pagination: {
      hasNextPage: boolean;
      nextPage: number | null;
    };
  };
};

export type CardActivities = Array<CardActivity>;

const ACTIVITY_PAGE_SIZE = '10';

export const useCardAggregateActivityQuery = (cardId: string) => {
  const company = useCompany();
  return useInfiniteQuery<CardActivity, RawAccountLoadResponse, unknown>({
    key: ['v2', 'card', cardId, 'aggregate-activity'],
    getRequest: (cursor) => {
      return {
        type: 'custom',
        fetchFunction: async () => {
          try {
            const response =
              await getV2ApiCompanyIdCardsCardIdAggregatedActivity({
                path: { companyId: company.id, cardId },
                query: {
                  pageNumber: cursor ? `${cursor}` : '1',
                  pageSize: ACTIVITY_PAGE_SIZE,
                },
              });

            if (response.statusCode !== 200) {
              throw new Error('Failed to fetch card activity');
            }

            return response.body as RawAccountLoadResponse;
          } catch (error) {
            throw customQueryRequestErrorFactory({
              status: 500,
              statusText: 'InternalServerError',
              data: error,
              method: 'GET',
              url: 'getV2ApiCompanyIdCardsCardIdAggregatedActivity',
            });
          }
        },
      };
    },
    getNextPageParam(data) {
      if (!data?.meta?.pagination?.hasNextPage) {
        return undefined;
      }
      return data.meta.pagination.nextPage ?? undefined;
    },
    reshapeData(data) {
      return data.data;
    },
  });
};

export const useInvalidateCardAggregateActivityQuery = () => {
  const queryClient = useQueryClient();
  return (cardId: string | null) => {
    if (!cardId) {
      return;
    }

    return queryClient.invalidateQueries([
      'v2',
      'card',
      cardId,
      'aggregate-activity',
    ]);
  };
};
